/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import {
  CommandBar,
  ICommandBarItemProps,
  Label,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import { spinnerStyles } from "Components/Common/SharedListStyles.styles";
import {
  bodyContainerStackStyles,
  commandBarStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { MostValuableProfessionalProgramProfileEditForm } from "Components/MostValuableProfessionalProgramProfileEditForm/MostValuableProfessionalProgramProfileEditForm";
import { MostValuableProfessionalRetireEditForm } from "Components/MostValuableProfessionalRetireEditForm/MostValuableProfessionalRetireEditForm";
import { OperationStatus } from "Constants/OperationStatus";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import { fetchProgramProfile } from "Dataflows/MVP/MVPAction";
import {
  getProgramProfile,
  getProgramProfileErrorMessage,
  getProgramProfileOperationStatus,
} from "Dataflows/MVP/ProgramProfile/IMVPProgramProfileSelector";
import { formatDate } from "Utility/FormatHelper";

import { IMostValuableProfessionalProgramProfileProps } from "./IMostValuableProfessionalProgramProfileProps";
import {
  bodyColumnStackItemStyles,
  bodyContainerStackTokens,
  bodyRowStackStyles,
  labelStyles,
} from "./MostValuableProfessionalProgramProfile.styles";
import { IMVPRetireProfileRequest } from "Interfaces/MVP/IMVPRetireProfileRequest";

const MostValuableProfessionalProgramProfile = (
  props: IMostValuableProfessionalProgramProfileProps
): ReactElement => {
  const { userProfileId } = props;

  const { isUserLoggedIn, loading, hasAnyRoles } = useAuth();
  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  const dispatch = useDispatch();
  const programProfile = useSelector(getProgramProfile);
  const programProfileOperationStatus = useSelector(getProgramProfileOperationStatus);
  const programProfileErrorMessage = useSelector(getProgramProfileErrorMessage);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isRetirePanelOpen, setIsRetirePanelOpen] = useState(false);

  useEffect(() => {
    if (!!userProfileId) {
      dispatch(fetchProgramProfile(userProfileId));
    }
  }, []);

  const editProgramInformationClicked = (): void => {
    setIsPanelOpen(true);
  };

  const editRetireClicked = (): void => {
    setIsRetirePanelOpen(true);
  };

  const formatDateOrUndefined = (date: Date | undefined): string => {
    if (!!date) {
      return formatDate(date);
    } else {
      return "";
    }
  };

  const { hasRole } = useAuth();
  const renderCommand = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [];
    if (hasRole(RolesEnum.MVP_REVIEWER) || hasRole(RolesEnum.MVP_PROGRAM_OWNER)) {
      items.push({
        key: "editProgramInformation",
        text: "Edit Program Details",
        iconProps: { iconName: "Edit" },
        onClick: (): void => {
          editProgramInformationClicked();
        },
      });
    }
    if (hasRole(RolesEnum.MVP_PROGRAM_OWNER) || hasRole(RolesEnum.MVP_BENEFITS_MANAGER)) {
      items.push({
        key: "editRetire",
        text: "Retire",
        iconProps: { iconName: "Edit" },
        onClick: (): void => {
          editRetireClicked();
        },
      });
    }
    return items;
  };

  if (programProfileOperationStatus?.read === OperationStatus.Pending) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (
    programProfileOperationStatus?.read === OperationStatus.Failed &&
    programProfileErrorMessage
  ) {
    return <div>{programProfileErrorMessage}</div>;
  }

  return (
    <>
      {!programProfile ? (
        <Spinner size={SpinnerSize.large} styles={spinnerStyles} />
      ) : (
        <>
          {
            <StackItem>
              <CommandBar
                items={renderCommand()}
                ariaLabel="Benefit actions"
                styles={commandBarStyles}
              />
            </StackItem>
          }
          <ContainerWrapper>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>Program Details</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Application Status</Label>
                  <Text>{programProfile?.applicationStatus}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Date Last Modified</Label>
                  <Text>{formatDateOrUndefined(programProfile?.dateLastModified)}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Date Submitted</Label>
                  <Text>{formatDateOrUndefined(programProfile?.dateSubmitted)}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Program Profile Status</Label>
                  <Text>{programProfile?.programProfileStatus}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Program End Date</Label>
                  <Text>{formatDateOrUndefined(programProfile?.programEndDate)}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>CPM</Label>
                  <Text>{programProfile?.cpm}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Last Invited Date</Label>
                  <Text>{formatDateOrUndefined(programProfile?.dateLastInvited)}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Award Category #1</Label>
                  <Text>{programProfile?.technologyAreaGroups?.[0]}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Technology Area #1</Label>
                  <Text>{programProfile?.technologyAreas?.[0]}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Award Category #2</Label>
                  <Text>{programProfile?.technologyAreaGroups?.[1]}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Technology Area #2</Label>
                  <Text>{programProfile?.technologyAreas?.[1]}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>First Award Date</Label>
                  <Text>{formatDateOrUndefined(programProfile?.firstAwardDate)}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Consecutive Awards</Label>
                  <Text>{programProfile?.consecutiveAwards}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>NDA Status</Label>
                  <Text>{programProfile?.ndaStatus}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Strengths</Label>
                  <Text>{programProfile?.strengths?.join(", ")}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Contributor ID</Label>
                  <Text>{programProfile?.microsoftReferralId}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>MVP Legacy ID</Label>
                  <Text>{programProfile?.mvpLegacyId}</Text>
                </StackItem>
              </Stack>
            </Stack>
          </ContainerWrapper>
        </>
      )}
      {isPanelOpen && (
        <MostValuableProfessionalProgramProfileEditForm
          isPanelOpen={isPanelOpen}
          setIsPanelOpen={setIsPanelOpen}
          defaultValues={{
            userProfileId,
            programProfileStatus: programProfile?.programProfileStatus,
            firstAwardDate: programProfile?.firstAwardDate
              ? new Date(programProfile?.firstAwardDate as Date)
              : undefined,
            cpm: programProfile?.cpm || "",
            strengths: programProfile?.strengths,
            availableCommunityProgramManagers: programProfile?.availableCommunityProgramManagers,
          }}
        />
      )}
      {isRetirePanelOpen && (
        <MostValuableProfessionalRetireEditForm
          isPanelOpen={isRetirePanelOpen}
          setIsPanelOpen={setIsRetirePanelOpen}
          defaultValues={
            {
              userProfileId: userProfileId,
            } as IMVPRetireProfileRequest
          }
        />
      )}
    </>
  );
};

export default MostValuableProfessionalProgramProfile;
